// An everyday object: a tennis ball, a tin can, a dice and so on. Plain
// React SVG, like practice-solid.tsx, and on the same slant: a box shows its
// front, its top and its right side; a can shows its round top. No word is
// written on the picture.

import type { ReactNode } from 'react';
import {
  EVERYDAY_OBJECT_HEIGHT,
  EVERYDAY_OBJECT_WIDTH,
  describeEverydayObject,
  type EverydayObject,
  type EverydayObjectSpec,
} from './practice-everyday-object-data';

const INK = '#2d4a3e';

const line = {
  stroke: INK,
  strokeWidth: 2.2,
  strokeLinejoin: 'round' as const,
  strokeLinecap: 'round' as const,
};

type Box = {
  x: number;
  y: number;
  width: number;
  height: number;
  // How far the back of the box sits to the right and up.
  dx: number;
  dy: number;
};

const point = (x: number, y: number) => `${String(x)},${String(y)}`;

// A box on the slant: its front, top and right side, each its own colour.
function Cuboid({
  box,
  front,
  top,
  side,
  children,
}: {
  box: Box;
  front: string;
  top: string;
  side: string;
  children?: ReactNode;
}) {
  const { x, y, width: w, height: h, dx, dy } = box;
  return (
    <g>
      <polygon
        data-face="top"
        points={[
          point(x, y),
          point(x + dx, y + dy),
          point(x + w + dx, y + dy),
          point(x + w, y),
        ].join(' ')}
        fill={top}
        {...line}
      />
      <polygon
        data-face="side"
        points={[
          point(x + w, y),
          point(x + w + dx, y + dy),
          point(x + w + dx, y + h + dy),
          point(x + w, y + h),
        ].join(' ')}
        fill={side}
        {...line}
      />
      <rect
        data-face="front"
        x={x}
        y={y}
        width={w}
        height={h}
        fill={front}
        {...line}
      />
      {children}
    </g>
  );
}

// Where a dot sits on a face of a box: `across` and `back` run from 0 to 1.
function onTop(box: Box, across: number, back: number) {
  return {
    x: box.x + across * box.width + back * box.dx,
    y: box.y + back * box.dy,
  };
}

function onSide(box: Box, down: number, back: number) {
  return {
    x: box.x + box.width + back * box.dx,
    y: box.y + down * box.height + back * box.dy,
  };
}

function Pip({ x, y }: { x: number; y: number }) {
  return <circle data-pip="true" cx={x} cy={y} r={4.2} fill={INK} />;
}

function Drawing({ object }: { object: EverydayObject }) {
  switch (object) {
    case 'tennis_ball':
      return (
        <g>
          <circle cx={80} cy={72} r={50} fill="#d4e157" {...line} />
          <path
            d="M 44 37 C 72 58 72 88 42 106"
            fill="none"
            stroke="#ffffff"
            strokeWidth={4}
          />
          <path
            d="M 116 37 C 88 58 88 88 118 106"
            fill="none"
            stroke="#ffffff"
            strokeWidth={4}
          />
        </g>
      );
    case 'orange':
      return (
        <g>
          <circle cx={80} cy={76} r={48} fill="#f39c32" {...line} />
          {[
            [62, 62],
            [92, 56],
            [104, 82],
            [70, 96],
            [86, 106],
            [56, 84],
          ].map(([x = 0, y = 0], index) => (
            <circle key={index} cx={x} cy={y} r={1.6} fill="#c66f12" />
          ))}
          <path d="M 80 30 L 82 22" {...line} fill="none" />
          <path
            d="M 82 24 Q 96 12 106 22 Q 94 30 82 24 Z"
            fill="#6aa86a"
            {...line}
          />
        </g>
      );
    case 'tin_can':
      return (
        <g>
          <path
            d="M 42 32 L 42 112 A 38 11 0 0 0 118 112 L 118 32 Z"
            fill="#c4c8cc"
            {...line}
          />
          <path
            d="M 42 50 L 42 94 A 38 11 0 0 0 118 94 L 118 50 A 38 11 0 0 1 42 50 Z"
            fill="#e0605a"
            {...line}
          />
          <ellipse cx={80} cy={32} rx={38} ry={11} fill="#dfe2e5" {...line} />
          <ellipse
            cx={80}
            cy={32}
            rx={31}
            ry={7.5}
            fill="none"
            stroke={INK}
            strokeWidth={1.2}
          />
        </g>
      );
    case 'tissue_box': {
      const box = { x: 28, y: 62, width: 90, height: 58, dx: 24, dy: -18 };
      const middle = onTop(box, 0.5, 0.5);
      return (
        <Cuboid box={box} front="#8fc1e3" top="#b9daf0" side="#6fa6cc">
          <ellipse
            cx={middle.x}
            cy={middle.y}
            rx={24}
            ry={5}
            fill="#ffffff"
            {...line}
            strokeWidth={1.5}
          />
          <path
            d={`M ${String(middle.x - 12)} ${String(middle.y)} Q ${String(middle.x - 6)} ${String(middle.y - 26)} ${String(middle.x + 4)} ${String(middle.y - 18)} Q ${String(middle.x + 10)} ${String(middle.y - 30)} ${String(middle.x + 14)} ${String(middle.y)} Z`}
            fill="#ffffff"
            {...line}
            strokeWidth={1.5}
          />
        </Cuboid>
      );
    }
    case 'cereal_box': {
      const box = { x: 46, y: 30, width: 62, height: 100, dx: 20, dy: -16 };
      return (
        <Cuboid box={box} front="#f2c94c" top="#f7dd8a" side="#d9a92a">
          <rect x={46} y={42} width={62} height={14} fill="#e0605a" />
          <path
            d="M 58 92 A 19 13 0 0 0 96 92 Z"
            fill="#ffffff"
            {...line}
            strokeWidth={1.5}
          />
          <circle cx={70} cy={88} r={3.5} fill="#c9893c" />
          <circle cx={79} cy={86} r={3.5} fill="#c9893c" />
          <circle cx={87} cy={89} r={3.5} fill="#c9893c" />
          <rect x={46} y={30} width={62} height={100} fill="none" {...line} />
        </Cuboid>
      );
    }
    case 'dice': {
      const box = { x: 40, y: 50, width: 68, height: 68, dx: 26, dy: -20 };
      const front = (across: number, down: number) => ({
        x: box.x + across * box.width,
        y: box.y + down * box.height,
      });
      return (
        <Cuboid box={box} front="#ffffff" top="#f4f6f4" side="#e3e8e3">
          {/* 5 on the front, 3 on the top, 2 on the side. */}
          {[
            front(0.25, 0.25),
            front(0.75, 0.25),
            front(0.5, 0.5),
            front(0.25, 0.75),
            front(0.75, 0.75),
            onTop(box, 0.2, 0.25),
            onTop(box, 0.5, 0.5),
            onTop(box, 0.8, 0.75),
            onSide(box, 0.3, 0.3),
            onSide(box, 0.7, 0.7),
          ].map((at, index) => (
            <Pip key={index} {...at} />
          ))}
        </Cuboid>
      );
    }
    case 'party_hat':
      return (
        <g>
          <path
            d="M 80 18 L 38 116 A 42 12 0 0 0 122 116 Z"
            fill="#8a5bb5"
            {...line}
          />
          <path
            d="M 66 51 Q 82 56 92 46 M 55 77 Q 82 85 104 72 M 45 100 Q 80 111 114 96"
            fill="none"
            stroke="#f2c94c"
            strokeWidth={5}
          />
          <path
            d="M 80 18 L 38 116 A 42 12 0 0 0 122 116 Z"
            fill="none"
            {...line}
          />
          <circle cx={80} cy={15} r={8} fill="#f2c94c" {...line} />
        </g>
      );
    case 'ice_cream_cone':
      return (
        <g>
          <path d="M 52 66 L 80 132 L 108 66 Z" fill="#e2b26b" {...line} />
          <path
            d="M 60 80 L 98 76 M 66 96 L 94 92 M 72 112 L 89 110 M 62 70 L 88 118 M 78 68 L 98 100"
            fill="none"
            stroke="#b9853c"
            strokeWidth={1.5}
          />
          <circle cx={80} cy={46} r={30} fill="#f7b6c8" {...line} />
          <path
            d="M 50 56 Q 58 66 66 58 Q 74 68 82 60 Q 90 68 98 58 Q 104 64 110 54"
            fill="#f7b6c8"
            {...line}
          />
        </g>
      );
    case 'building_block': {
      const box = { x: 32, y: 64, width: 92, height: 52, dx: 24, dy: -18 };
      return <Cuboid box={box} front="#e0605a" top="#ef948f" side="#b84a45" />;
    }
  }
}

export function EverydayObjectView({ thing }: { thing: EverydayObjectSpec }) {
  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describeEverydayObject(thing)}
        data-object={thing.object}
        viewBox={`0 0 ${String(EVERYDAY_OBJECT_WIDTH)} ${String(EVERYDAY_OBJECT_HEIGHT)}`}
        className="mx-auto block h-auto w-full"
        style={{
          maxWidth: `${String(Math.round(EVERYDAY_OBJECT_WIDTH * 1.4))}px`,
        }}
      >
        <Drawing object={thing.object} />
      </svg>
    </figure>
  );
}
